import React, { useState, useEffect } from "react";
import { Download, Pencil, Trash2, ExternalLink, BellOff } from "lucide-react";
import Loading from "../Loading";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { FilePreviewEdit } from "./FilePreviewEdit";
import { Button } from "../ui/button";
import { DeleteDialog } from "../DeleteDialog/DeleteDialog";
import {
  getCredentialFileUrls,
  previewRenditionForFileName,
} from "@/ajax/documents/credential-file-urls";
import PDFViewer from "../PDFViewer/PDFViewer";
import ImageViewer from "../MediaViewer/ImageViewer";
import { parse, parseISO } from "date-fns";
import { downloadFile } from "@/ajax/documents/downloadFile";
import { generateDocumentName } from "@/features/documents/utils/generateDocumentName";
import { useTRPC } from "@/trpc";
import { FileType } from "@/features/files/types/file-type";

type File = FileType & {
  url?: string;
};

async function updateVersionSource({
  version,
  userId,
  organizationBusinessUnitId,
}: {
  version: File;
  userId: string;
  organizationBusinessUnitId?: string;
}): Promise<File> {
  try {
    const [file] = await getCredentialFileUrls({
      switchUserId: userId,
      organizationBusinessUnitId,
      versions: [
        {
          versionId: version.id,
          rendition: previewRenditionForFileName(version.file_name),
        },
      ],
    });
    return { ...version, url: file.url ?? undefined };
  } catch (error) {
    // Still show the details and actions; the preview pane says it could not
    // load instead of spinning forever.
    console.error("Error loading file preview:", error);
    return { ...version, url: undefined };
  }
}
const formatDateTime = (datetimeString: string) => {
  const date = new Date(datetimeString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDate = (dateString: string) => {
  const date = parse(dateString, "yyyy-MM-dd", new Date());
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
};

const FileVersionPreview = ({
  file,
  userId,
  onDelete,
  editable = true,
  organizationBusinessUnitId,
}: {
  file: File;
  userId: string;
  onDelete: () => void;
  editable?: boolean;
  organizationBusinessUnitId?: string;
}) => {
  const [versionSource, setSelectedVersion] = useState<null | File>(null);
  const [showEditFile, setShowEditFile] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const filesQuery = useQuery(
    trpc.credentialFiles.getFilesCheckPermission.queryOptions(
      {
        id: file.id,
        switchUserId: userId,
        organizationBusinessUnitId,
      },
      {
        retry: 2,
      },
    ),
  );

  const deleteMutation = useMutation(
    trpc.credentialFiles.deleteFile.mutationOptions({
      onError: (error) => {
        console.error("Error deleting file:", error);
        alert("Failed to delete file. Please try again.");
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.credentialFiles.getFiles.queryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.credentialFiles.getFilesCheckPermission.queryKey(),
        });
        queryClient.invalidateQueries({ queryKey: ["renewals"] });
        onDelete();
      },
    }),
  );

  const snoozeMutation = useMutation(
    trpc.credentialFiles.snoozeNotifications.mutationOptions({
      onError: (error) => {
        console.error("Error snoozing notifications:", error);
        alert("Failed to snooze notifications. Please try again.");
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.credentialFiles.getFiles.queryKey(),
        });
      },
    }),
  );

  useEffect(() => {
    (async () => {
      if (!filesQuery.data?.data?.[0]) {
        return;
      }

      const version = await updateVersionSource({
        version: filesQuery.data.data[0],
        userId,
        organizationBusinessUnitId,
      });
      setSelectedVersion(version);
    })();
  }, [filesQuery.data, userId, organizationBusinessUnitId]);

  const handleDelete = async () => {
    deleteMutation.mutate({
      id: file.id,
      switchUserId: userId,
      organizationBusinessUnitId,
    });
  };

  const handleDownload = async () => {
    if (versionSource?.url) {
      const fileName = generateDocumentName(versionSource);

      downloadFile({
        userId,
        fileId: versionSource.id,
        fileName,
        organizationBusinessUnitId,
      });
    }
  };

  const handleSnoozeNotifications = async () => {
    snoozeMutation.mutate({
      fileId: file.user_credentials_file_id,
      switchUserId: userId,
      organizationBusinessUnitId,
    });
  };

  const _renderOCRData = (data: unknown, level = 0) => {
    if (typeof data !== "object" || data === null) {
      return (
        <span className="text-gray-800">
          {data == null ? "None" : JSON.stringify(data)}
        </span>
      );
    }

    return (
      <ul className={`space-y-1 ${level === 0 ? "mt-2" : "ml-4"}`}>
        {Object.entries(data).map(([key, value]) => (
          <li key={key} className="font-mono">
            <span className="font-medium text-blue-600 break-all">{key}:</span>{" "}
            {Array.isArray(value) ? (
              <span>{_renderOCRData(value, level + 1)}</span>
            ) : typeof value === "object" && value !== null ? (
              <span>{_renderOCRData(value, level + 1)}</span>
            ) : (
              <span className="text-gray-800 break-all">
                {value == null ? "None" : JSON.stringify(value)}
              </span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const renderPreview = (version: File) => {
    const extension = version.file_name.split(".").pop();
    const isPDF = extension?.toLowerCase() === "pdf";
    // NOTE: svg is intentionally excluded — SVGs can carry <script> and must
    // never be rendered inline. They are also rejected at upload by the
    // scanner and the credentials bucket allowed_mime_types.
    const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(
      extension?.toLowerCase() || "",
    );

    if (isPDF && version.url) {
      return <PDFViewer url={version.url} />;
    } else if (isImage && version.url) {
      return (
        <ImageViewer
          url={version.url}
          alt={version.generated_title || version.file_name || "Image"}
        />
      );
    } else if (version.url) {
      // Fallback for other file types (keeping original img for non-image files)
      return (
        <img
          src={version.url}
          alt={version.generated_title || version.file_name || "File"}
          className="max-w-full max-h-[70vh] object-contain mx-auto rounded"
        />
      );
    } else {
      return <div>Unable to load preview</div>;
    }
  };

  if (!versionSource) {
    return (
      <div className="flex w-full item-center justify-center">
        <Loading className="w-20 h-20" />
      </div>
    );
  }

  return (
    <div className={`w-full`}>
      <div className="flex flex-col md:flex-row md:space-x-4">
        <div className="w-full md:w-1/2 order-2 md:order-1 mb-4 md:mb-0">
          {renderPreview(versionSource)}
        </div>
        <div className="w-full md:w-1/2 order-1 md:order-2 mb-4 md:mb-0">
          <div className="flex justify-center md:justify-between items-start mb-4">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-medium break-words">
                {versionSource.generated_title || versionSource.file_name}
              </h3>
              {versionSource.generated_title && (
                <h4 className="text-md mt-1">
                  <span className="text-gray-500 break-all">
                    ({versionSource.file_name})
                  </span>
                </h4>
              )}
              <h4 className="mt-1">{versionSource.type_name}</h4>
              <p className="mt-1">{formatDateTime(versionSource.created_at)}</p>
              {versionSource.expires_at && (
                <div className="mt-1">
                  <p>Expires: {formatDate(versionSource.expires_at)}</p>
                  {versionSource.expiry_notifications_snooze_until && (
                    <p className="text-sm text-gray-500">
                      Notifications snoozed until:{" "}
                      {formatDate(
                        versionSource.expiry_notifications_snooze_until,
                      )}
                    </p>
                  )}
                </div>
              )}
              <div className="mt-4 flex flex-col space-y-2 max-w-sm mx-auto md:mx-0">
                {editable && versionSource.renew_url && (
                  <a
                    href={versionSource.renew_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center space-x-2 px-3 py-1.5 bg-primary text-primary-foreground text-sm font-medium rounded hover:bg-primary/90 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Renew</span>
                  </a>
                )}

                {editable && (
                  <Button
                    className="w-full flex items-center justify-center space-x-2"
                    variant="outline"
                    onClick={() => setShowEditFile(true)}
                  >
                    <Pencil className="w-4 h-4" />
                    <span>Edit Details</span>
                  </Button>
                )}
                {editable &&
                  versionSource.expiry_notifications_enabled &&
                  versionSource.expires_at &&
                  parseISO(versionSource.expires_at) > new Date() &&
                  (!versionSource.expiry_notifications_snooze_until ||
                    parseISO(versionSource.expiry_notifications_snooze_until) <=
                      new Date()) && (
                    <Button
                      className="w-full flex items-center justify-center space-x-2"
                      variant="outline"
                      onClick={handleSnoozeNotifications}
                      disabled={snoozeMutation.isPending}
                    >
                      {snoozeMutation.isPending ? (
                        <Loading className="w-4 h-4" />
                      ) : (
                        <BellOff className="w-4 h-4" />
                      )}
                      <span>Snooze Notifications (2 weeks)</span>
                    </Button>
                  )}
                <Button
                  className="w-full flex items-center justify-center space-x-2"
                  variant="outline"
                  onClick={handleDownload}
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </Button>

                {editable && (
                  <Button
                    className="w-full flex items-center justify-center space-x-2 text-red-500"
                    variant="outline"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEditFile && (
        <FilePreviewEdit
          file={versionSource}
          userId={userId}
          organizationBusinessUnitId={organizationBusinessUnitId}
          onClose={() => setShowEditFile(false)}
        />
      )}

      <DeleteDialog
        description={`This will permanently delete the file and and all associated renewals. Please make sure this is correct. This action cannot be undone.`}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default FileVersionPreview;
